import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Loader2, Plus, ArrowUp, ArrowDown, X } from 'lucide-react';
import { priceBookAssembliesApi, priceBookItemsApi } from '../api';
import { centsToDollarString } from '../constants';
import { UNIT_TYPE_OPTIONS, unitAbbr } from '../priceBookConstants';
import { CategorySelect } from './CategorySelect';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls = 'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const Label = ({ children }) => <label className="text-sm text-gray-300 mb-1 block font-medium">{children}</label>;

// Live response-style preview mirroring backend rules: missing prices are never $0
const preview = (components, itemMap) => {
  const t = { internal: 0, investor: 0, standard: 0 };
  const miss = { internal: 0, investor: 0, standard: 0 };
  const fields = { internal: 'internal_unit_cost_cents', investor: 'investor_unit_price_cents', standard: 'standard_unit_price_cents' };
  components.forEach((c) => {
    const it = itemMap[c.item_id];
    const qty = parseFloat(c.quantity_decimal) || 0;
    Object.entries(fields).forEach(([lvl, f]) => {
      const price = it ? it[f] : null;
      if (price === null || price === undefined) miss[lvl] += 1;
      else t[lvl] += Math.round(qty * price);
    });
  });
  return { t, miss };
};

export const AssemblyFormDialog = ({ open, onOpenChange, assembly, onSaved, defaultCategory = '' }) => {
  const isNew = !assembly;
  const archived = assembly?.is_archived;
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [f, setF] = useState({ name: '', description: '', category: '', primary_unit_type: '', custom_primary_unit_label: '', internal_notes: '' });
  const [components, setComponents] = useState([]);

  useEffect(() => {
    if (!open) return;
    priceBookItemsApi.list({ archived: false }).then((r) => setItems(r.data)).catch(() => {});
    setItemSearch('');
    if (assembly) {
      setF({
        name: assembly.name || '', description: assembly.description || '', category: assembly.category || '',
        primary_unit_type: assembly.primary_unit_type || '', custom_primary_unit_label: assembly.custom_primary_unit_label || '',
        internal_notes: assembly.internal_notes || '',
      });
      setComponents((assembly.components || []).map((c) => ({ id: c.id, item_id: c.item_id, quantity_mode: c.quantity_mode, quantity_decimal: c.quantity_decimal, notes: c.notes || '', item: c.item })));
    } else {
      setF({ name: '', description: '', category: defaultCategory, primary_unit_type: '', custom_primary_unit_label: '', internal_notes: '' });
      setComponents([]);
    }
  }, [open, assembly, defaultCategory]);

  const itemMap = useMemo(() => {
    const m = {};
    items.forEach((i) => { m[i.id] = i; });
    components.forEach((c) => { if (c.item && !m[c.item_id]) m[c.item_id] = c.item; });
    return m;
  }, [items, components]);

  const pv = useMemo(() => preview(components, itemMap), [components, itemMap]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setComp = (idx, k, v) => setComponents((arr) => arr.map((c, i) => (i === idx ? { ...c, [k]: v } : c)));
  const move = (idx, dir) => setComponents((arr) => {
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return arr;
    const copy = [...arr];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    return copy;
  });
  const remove = (idx) => setComponents((arr) => arr.filter((_, i) => i !== idx));

  const addComponent = (item) => {
    setComponents((arr) => [...arr, { id: null, item_id: item.id, quantity_mode: 'fixed', quantity_decimal: item.default_quantity_decimal || '1', notes: '', item }]);
    setItemSearch('');
  };

  const searchResults = itemSearch.trim()
    ? items.filter((i) => `${i.name} ${i.category || ''} ${i.item_code || ''}`.toLowerCase().includes(itemSearch.toLowerCase())).slice(0, 8)
    : [];

  const save = async () => {
    if (busy) return;
    if (!f.name.trim()) { toast.error('Assembly name is required'); return; }
    for (const c of components) {
      const q = parseFloat(c.quantity_decimal);
      if (!(q > 0)) { toast.error('Every component needs a quantity greater than 0'); return; }
    }
    setBusy(true);
    try {
      const body = {
        name: f.name, description: f.description || null, category: f.category || null,
        primary_unit_type: f.primary_unit_type || null,
        custom_primary_unit_label: f.primary_unit_type === 'other' ? (f.custom_primary_unit_label || null) : null,
        internal_notes: f.internal_notes || null,
        components: components.map((c) => ({ id: c.id || undefined, item_id: c.item_id, quantity_mode: c.quantity_mode, quantity_decimal: String(c.quantity_decimal), notes: c.notes || null })),
      };
      const res = isNew ? await priceBookAssembliesApi.create(body) : await priceBookAssembliesApi.update(assembly.id, body);
      toast.success(isNew ? 'Assembly created' : 'Assembly saved');
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save assembly');
    } finally { setBusy(false); }
  };

  const action = async (fn, msg) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Action failed');
    } finally { setBusy(false); }
  };

  const pvVal = (lvl) => (pv.miss[lvl] > 0 ? `Incomplete (${pv.miss[lvl]} missing)` : centsToDollarString(pv.t[lvl]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="assembly-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New Assembly' : archived ? 'Archived Assembly' : 'Edit Assembly'}</DialogTitle>
          <DialogDescription className="text-gray-500">
            Reusable group of Price Book items. The future Quote Builder will expand assemblies into quote line items.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name *</Label>
              <Input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Complete LVP Room" disabled={archived} className={inputCls} data-testid="assembly-name" />
            </div>
            <div>
              <Label>Category</Label>
              <CategorySelect value={f.category} onChange={(v) => set('category', v)} disabled={archived} data-testid="assembly-category" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Input value={f.description} onChange={(e) => set('description', e.target.value)} disabled={archived} className={inputCls} data-testid="assembly-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Primary Unit Type</Label>
              <select value={f.primary_unit_type} onChange={(e) => set('primary_unit_type', e.target.value)} disabled={archived} className={selectCls} data-testid="assembly-primary-unit">
                <option value="">None</option>
                {UNIT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {f.primary_unit_type === 'other' && (
              <div>
                <Label>Custom Unit Label</Label>
                <Input value={f.custom_primary_unit_label} onChange={(e) => set('custom_primary_unit_label', e.target.value)} disabled={archived} className={inputCls} data-testid="assembly-custom-unit" />
              </div>
            )}
          </div>

          {/* Components */}
          <div>
            <Label>Components</Label>
            {components.length === 0 && <p className="text-xs text-amber-400/90 mb-2" data-testid="assembly-empty-warning">This assembly has no components yet.</p>}
            <div className="space-y-2">
              {components.map((c, idx) => {
                const it = itemMap[c.item_id];
                return (
                  <div key={c.id || `new-${idx}`} className="bg-[#1A1A1A] border border-gray-800 rounded-md p-3" data-testid={`assembly-component-${idx}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <span className="text-sm text-white font-medium">{it?.name || 'Unknown item'}</span>
                        {it?.is_archived && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-red-900/40 text-red-400 border-red-800 ml-2">Archived Item</span>}
                        <div className="text-[11px] text-gray-500">
                          {it ? `${unitAbbr(it)} · Investor ${it.investor_unit_price_cents != null ? centsToDollarString(it.investor_unit_price_cents) : 'Not set'} · Standard ${it.standard_unit_price_cents != null ? centsToDollarString(it.standard_unit_price_cents) : 'Not set'}` : ''}
                        </div>
                      </div>
                      {!archived && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => move(idx, -1)} className="p-1 text-gray-500 hover:text-white" data-testid={`comp-up-${idx}`}><ArrowUp size={14} /></button>
                          <button onClick={() => move(idx, 1)} className="p-1 text-gray-500 hover:text-white" data-testid={`comp-down-${idx}`}><ArrowDown size={14} /></button>
                          <button onClick={() => remove(idx)} className="p-1 text-red-400 hover:text-red-300" data-testid={`comp-remove-${idx}`}><X size={14} /></button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <select value={c.quantity_mode} onChange={(e) => setComp(idx, 'quantity_mode', e.target.value)} disabled={archived} className={selectCls} data-testid={`comp-mode-${idx}`}>
                        <option value="fixed">Fixed Quantity</option>
                        <option value="per_primary_unit">Per Primary Unit</option>
                      </select>
                      <Input type="number" min="0" step="any" value={c.quantity_decimal} onChange={(e) => setComp(idx, 'quantity_decimal', e.target.value)} disabled={archived} className={inputCls} data-testid={`comp-qty-${idx}`} />
                      <Input value={c.notes} onChange={(e) => setComp(idx, 'notes', e.target.value)} placeholder="Notes" disabled={archived} className={inputCls} data-testid={`comp-notes-${idx}`} />
                    </div>
                  </div>
                );
              })}
            </div>
            {!archived && (
              <div className="mt-2 relative">
                <Input value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder="Search items to add…" className={inputCls} data-testid="assembly-item-search" />
                {searchResults.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-[#1A1A1A] border border-gray-700 rounded-md max-h-56 overflow-y-auto shadow-xl">
                    {searchResults.map((it) => (
                      <button key={it.id} onClick={() => addComponent(it)} className="w-full text-left px-3 py-2 hover:bg-[#252525] border-b border-gray-800/60 last:border-0" data-testid={`item-pick-${it.id}`}>
                        <div className="text-sm text-white flex items-center gap-2"><Plus size={12} className="text-[#D4AF37]" /> {it.name}</div>
                        <div className="text-[11px] text-gray-500 ml-5">
                          {it.category || 'Uncategorized'} · {unitAbbr(it)} · Price {it.standard_unit_price_cents != null ? centsToDollarString(it.standard_unit_price_cents) : 'Not set'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Live preview at primary quantity 1 */}
          <div className="bg-[#1A1A1A] border border-gray-800 rounded-md p-3" data-testid="assembly-preview">
            <div className="text-xs text-gray-500 mb-2">Pricing preview at primary quantity 1 — missing component prices are never counted as $0.</div>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div><span className="text-gray-500 block text-xs">Price</span><span className={pv.miss.standard ? 'text-amber-400' : 'text-gray-100 font-semibold'} data-testid="preview-standard">{pvVal('standard')}</span></div>
            </div>
          </div>

          <div>
            <Label>Internal Notes (internal only)</Label>
            <Input value={f.internal_notes} onChange={(e) => set('internal_notes', e.target.value)} disabled={archived} className={inputCls} data-testid="assembly-notes" />
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-gray-800">
          {archived ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300">Close</Button>
              <Button onClick={() => action(() => priceBookAssembliesApi.restore(assembly.id), 'Assembly restored')} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="assembly-restore">Restore Assembly</Button>
            </>
          ) : (
            <>
              {!isNew && (
                <Button variant="outline" onClick={() => action(() => priceBookAssembliesApi.archive(assembly.id), 'Assembly archived')} disabled={busy} className="border-red-900 text-red-400 mr-auto" data-testid="assembly-archive">Archive</Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-gray-700 text-gray-300">Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="assembly-save">
                {busy && <Loader2 size={14} className="animate-spin" />} {isNew ? 'Create Assembly' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
